<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import LabelsAPI from 'dashboard/api/labels';
import OnboardingAPI from 'dashboard/api/onboarding';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import OptionalStepGate from './OptionalStepGate.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  progress: { type: Object, required: true },
});
const emit = defineEmits(['refresh', 'update:can-advance']);

const { t } = useI18n();

const isStepAlreadyCompleted = computed(() =>
  props.progress.completed_steps.includes(10)
);

// Backed by the real account Label pool (same resource as Settings > Labels)
// instead of a wizard-only draft, same as Step09Csat — labels created here are
// real immediately, and labels added/removed elsewhere show up here too. A
// single step covers both conversation and contact labels since they're the
// same underlying resource with no field distinguishing one from the other.
const labels = ref([]);
const newLabel = ref('');
const inputError = ref('');

const hasDraft = computed(() => labels.value.length > 0);
const isValid = computed(() => labels.value.length > 0);
watch(isValid, value => emit('update:can-advance', value), { immediate: true });

const markStepCompleted = async () => {
  try {
    await OnboardingAPI.update({
      completed_steps: [...new Set([...props.progress.completed_steps, 10])],
    });
    emit('refresh');
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
};

const applyLabels = data => {
  labels.value = (data?.payload || []).map(label => ({
    id: label.id,
    title: label.title,
  }));
};

const addLabel = async () => {
  const title = newLabel.value.trim().toLowerCase();
  inputError.value = '';

  if (!title || title.length < 2 || title.includes(' ')) {
    inputError.value = t('ONBOARDING.LABEL_INPUT.INVALID');
    return;
  }
  if (labels.value.some(label => label.title === title)) {
    inputError.value = t('ONBOARDING.LABEL_INPUT.DUPLICATE');
    return;
  }

  try {
    const { data } = await LabelsAPI.create({ title });
    labels.value = [...labels.value, { id: data.id, title: data.title }];
    newLabel.value = '';
    if (!isStepAlreadyCompleted.value) await markStepCompleted();
  } catch (error) {
    inputError.value =
      error?.response?.data?.message || t('ONBOARDING.STEP_10.CREATE_ERROR');
  }
};

const removeLabel = async label => {
  try {
    await LabelsAPI.delete(label.id);
    labels.value = labels.value.filter(item => item.id !== label.id);
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
};

// Re-syncs when labels are added/edited/removed from another screen (e.g.
// Settings > Labels) so both views stay live.
const refreshFromServer = async () => {
  try {
    const { data } = await LabelsAPI.get();
    applyLabels(data);
  } catch (error) {
    // Ignore — a background refresh failing shouldn't disrupt an in-progress edit
  }
};

onMounted(async () => {
  try {
    const { data } = await LabelsAPI.get();
    applyLabels(data);
    if (labels.value.length && !isStepAlreadyCompleted.value) {
      await markStepCompleted();
    }
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
  emitter.on(BUS_EVENTS.LABEL_CHANGED, refreshFromServer);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.LABEL_CHANGED, refreshFromServer);
});
</script>

<template>
  <div
    class="flex flex-col items-center justify-start w-full h-full p-8 md:px-16"
  >
    <div class="flex flex-col items-start w-full max-w-2xl gap-4 text-left">
      <div class="flex items-center flex-wrap gap-3">
        <h1 class="text-2xl font-semibold text-n-slate-12">
          {{ t('ONBOARDING.STEP_10.TITLE') }}
        </h1>
        <a
          href="https://help.leadster.com.br/article/422-etiquetas-categorizando-e-priorizando-contatos-e-conversas"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-1 px-3 py-1 text-xs rounded-full shrink-0 bg-n-brand/10 text-n-brand hover:brightness-110"
        >
          <i class="i-lucide-circle-help text-sm" />
          {{ t('ONBOARDING.STEP_10.TUTORIAL_LINK') }}
        </a>
      </div>
      <p class="text-n-slate-12">
        {{ t('ONBOARDING.STEP_10.SUBTITLE') }}
        <span class="font-normal text-n-slate-10">{{
          t('ONBOARDING.OPTIONAL_BADGE')
        }}</span>
      </p>
      <p class="text-n-slate-11">{{ t('ONBOARDING.STEP_10.DESCRIPTION') }}</p>

      <OptionalStepGate
        :step="10"
        :progress="progress"
        :has-draft="hasDraft"
        :question="t('ONBOARDING.STEP_10.GATE_QUESTION')"
        :now-label="t('ONBOARDING.STEP_10.GATE_NOW')"
        :now-description="t('ONBOARDING.STEP_10.GATE_NOW_DESCRIPTION')"
        :later-label="t('ONBOARDING.STEP_10.GATE_LATER')"
        :later-description="t('ONBOARDING.STEP_10.GATE_LATER_DESCRIPTION')"
        @refresh="emit('refresh')"
        @update:can-advance="emit('update:can-advance', $event)"
      >
        <div class="flex flex-col w-full gap-3">
          <div class="flex items-start gap-2">
            <Input
              v-model="newLabel"
              :placeholder="t('ONBOARDING.STEP_10.PLACEHOLDER')"
              :message="inputError"
              :message-type="inputError ? 'error' : 'info'"
              class="flex-1"
              @keyup.enter="addLabel"
            />
            <Button
              type="button"
              :label="t('ONBOARDING.LABEL_INPUT.ADD')"
              @click="addLabel"
            />
          </div>

          <div v-if="labels.length" class="flex flex-wrap gap-2">
            <span
              v-for="label in labels"
              :key="label.id"
              class="flex items-center gap-1 px-2 py-1 text-sm rounded-md bg-n-alpha-2 text-n-slate-12"
            >
              {{ label.title }}
              <button
                type="button"
                class="i-lucide-x text-xs text-n-slate-11"
                :aria-label="t('ONBOARDING.LABEL_INPUT.REMOVE')"
                @click="removeLabel(label)"
              />
            </span>
          </div>
        </div>
      </OptionalStepGate>
    </div>
  </div>
</template>
